import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  RefreshCw, 
  TrendingUp, 
  TrendingDown, 
  BarChart3, 
  Clock,
  ChevronLeft,
  ChevronRight,
  Filter,
  ShieldAlert
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ReturnTrackingAdmin = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [isAdmin, setIsAdmin] = useState(null); // null = checking, true/false = result
  const [stats, setStats] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [pending, setPending] = useState({ pending: [], total_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ direction: '', minZscore: '' });
  const limit = 20;

  // Check admin status on mount
  useEffect(() => {
    const checkAdmin = async () => {
      if (!token) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/api/auth/admin-check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(response.data.is_admin);
        if (!response.data.is_admin) {
          setLoading(false);
        }
      } catch (error) {
        setIsAdmin(false);
        setLoading(false);
      }
    };
    checkAdmin();
  }, [token]);

  const getAuthHeaders = useCallback(() => {
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;

    try {
      const response = await axios.get(`${API_BASE}/api/admin/returns/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch stats');
    }
  }, [isAdmin, getAuthHeaders]);

  const fetchAnomalies = useCallback(async () => {
    if (!isAdmin) return;

    try {
      const params = {
        limit,
        offset: page * limit
      };
      if (filter.direction) params.direction = filter.direction;
      if (filter.minZscore) params.min_zscore = parseFloat(filter.minZscore);

      const response = await axios.get(`${API_BASE}/api/admin/returns/anomalies`, {
        headers: getAuthHeaders(),
        params
      });
      setAnomalies(response.data.anomalies);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch anomalies');
    }
  }, [isAdmin, page, filter, getAuthHeaders]);

  const fetchPending = useCallback(async () => {
    if (!isAdmin) return;

    try {
      const response = await axios.get(`${API_BASE}/api/admin/returns/pending`, {
        headers: getAuthHeaders()
      });
      setPending(response.data);
    } catch (error) {
      console.error('Failed to fetch pending');
    }
  }, [isAdmin, getAuthHeaders]);

  const triggerUpdate = async () => {
    setUpdating(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/admin/returns/trigger-update?limit=50`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Updated ${response.data.result.updated} anomalies`);
      await Promise.all([fetchStats(), fetchAnomalies(), fetchPending()]);
    } catch (error) {
      toast.error('Failed to trigger update');
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    if (isAdmin === true) {
      const loadData = async () => {
        setLoading(true);
        await Promise.all([fetchStats(), fetchAnomalies(), fetchPending()]);
        setLoading(false);
      };
      loadData();
    }
  }, [isAdmin, fetchStats, fetchAnomalies, fetchPending]);

  useEffect(() => {
    if (isAdmin === true) {
      fetchAnomalies();
    }
  }, [page, filter, isAdmin, fetchAnomalies]);

  const formatReturn = (value) => {
    if (value === null || value === undefined) return '-';
    const color = value >= 0 ? '#22c55e' : '#ef4444';
    const sign = value >= 0 ? '+' : '';
    return (
      <span style={{ color, fontWeight: 600 }}>
        {sign}{value.toFixed(2)}%
      </span>
    );
  };

  const formatWinRate = (value) => {
    if (value === null || value === undefined) return '-';
    const color = value >= 50 ? '#22c55e' : value >= 40 ? '#fbbf24' : '#ef4444';
    return <span style={{ color, fontWeight: 600 }}>{value.toFixed(1)}%</span>;
  };

  // Show unauthorized message if not admin
  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#0B0F1A' }}>
        <div 
          className="max-w-md w-full rounded-xl p-8 text-center"
          style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <ShieldAlert className="h-16 w-16 mx-auto mb-4" style={{ color: '#ef4444' }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: '#F4F4F5' }}>Access Denied</h2>
          <p className="mb-6" style={{ color: '#94A3B8' }}>
            You don't have permission to access this page. Please log in with an admin account.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 rounded-lg font-medium"
            style={{ backgroundColor: '#3B82F6', color: 'white' }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading || isAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F1A' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#0B0F1A', color: '#F4F4F5' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Anomaly Return Tracking</h1>
            <p className="text-sm" style={{ color: '#64748B' }}>
              Track forward returns (1D, 5D, 10D) for detected anomalies
            </p>
          </div>
        </div>
        <button
          onClick={triggerUpdate}
          disabled={updating}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all disabled:opacity-50"
          style={{ backgroundColor: '#3B82F6', color: 'white' }}
        >
          <RefreshCw className={`h-4 w-4 ${updating ? 'animate-spin' : ''}`} />
          {updating ? 'Updating...' : 'Update Returns'}
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Overall Stats */}
          <div 
            className="rounded-xl p-6"
            style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5" style={{ color: '#3B82F6' }} />
              <h3 className="font-semibold">Overall Statistics</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span style={{ color: '#94A3B8' }}>Total Tracked</span>
                <span className="font-mono font-semibold">{stats.overall.total_tracked}</span>
              </div>
              <div className="h-px" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
              <div className="grid grid-cols-5 gap-2 text-center">
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>1D Avg</div>
                  {formatReturn(stats.overall.avg_returns['1d'])}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>5D Avg</div>
                  {formatReturn(stats.overall.avg_returns['5d'])}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>10D Avg</div>
                  {formatReturn(stats.overall.avg_returns['10d'])}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>30D Avg</div>
                  {formatReturn(stats.overall.avg_returns['30d'])}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>60D Avg</div>
                  {formatReturn(stats.overall.avg_returns['60d'])}
                </div>
              </div>
              <div className="h-px" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
              <div className="grid grid-cols-5 gap-2 text-center">
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>1D Win</div>
                  {formatWinRate(stats.overall.win_rates['1d'])}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>5D Win</div>
                  {formatWinRate(stats.overall.win_rates['5d'])}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>10D Win</div>
                  {formatWinRate(stats.overall.win_rates['10d'])}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>30D Win</div>
                  {formatWinRate(stats.overall.win_rates['30d'])}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>60D Win</div>
                  {formatWinRate(stats.overall.win_rates['60d'])}
                </div>
              </div>
            </div>
          </div>

          {/* Up Moves - LONG Positions */}
          <div 
            className="rounded-xl p-6"
            style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5" style={{ color: '#22c55e' }} />
              <h3 className="font-semibold">LONG Positions</h3>
              <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>Up Signals</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span style={{ color: '#94A3B8' }}>Count</span>
                <span className="font-mono font-semibold">{stats.by_direction.up_moves.count}</span>
              </div>
              <div className="h-px" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
              <div className="grid grid-cols-5 gap-2 text-center">
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>1D P&L</div>
                  {formatReturn(stats.by_direction.up_moves.avg_1d)}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>5D P&L</div>
                  {formatReturn(stats.by_direction.up_moves.avg_5d)}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>10D P&L</div>
                  {formatReturn(stats.by_direction.up_moves.avg_10d)}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>30D P&L</div>
                  {formatReturn(stats.by_direction.up_moves.avg_30d)}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>60D P&L</div>
                  {formatReturn(stats.by_direction.up_moves.avg_60d)}
                </div>
              </div>
            </div>
          </div>

          {/* Down Moves - SHORT Positions */}
          <div 
            className="rounded-xl p-6"
            style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="h-5 w-5" style={{ color: '#ef4444' }} />
              <h3 className="font-semibold">SHORT Positions</h3>
              <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }}>Down Signals</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span style={{ color: '#94A3B8' }}>Count</span>
                <span className="font-mono font-semibold">{stats.by_direction.down_moves.count}</span>
              </div>
              <div className="h-px" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
              <div className="grid grid-cols-5 gap-2 text-center">
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>1D P&L</div>
                  {formatReturn(stats.by_direction.down_moves.avg_1d)}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>5D P&L</div>
                  {formatReturn(stats.by_direction.down_moves.avg_5d)}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>10D P&L</div>
                  {formatReturn(stats.by_direction.down_moves.avg_10d)}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>30D P&L</div>
                  {formatReturn(stats.by_direction.down_moves.avg_30d)}
                </div>
                <div>
                  <div className="text-xs mb-1" style={{ color: '#64748B' }}>60D P&L</div>
                  {formatReturn(stats.by_direction.down_moves.avg_60d)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Updates */}
      {pending.total_pending > 0 && (
        <div 
          className="rounded-xl p-4 mb-6 flex items-center gap-3"
          style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)' }}
        >
          <Clock className="h-5 w-5" style={{ color: '#fbbf24' }} />
          <span>
            <strong>{pending.total_pending}</strong> anomalies have pending return calculations
          </span>
        </div>
      )}

      {/* Filters */}
      <div 
        className="rounded-xl p-4 mb-6 flex items-center gap-4"
        style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <Filter className="h-5 w-5" style={{ color: '#64748B' }} />
        <select
          value={filter.direction}
          onChange={(e) => { setFilter({ ...filter, direction: e.target.value }); setPage(0); }}
          className="px-3 py-2 rounded-lg text-sm"
          style={{ backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: '#F4F4F5' }}
        >
          <option value="">All Directions</option>
          <option value="up">Up Moves Only</option>
          <option value="down">Down Moves Only</option>
        </select>
        <input
          type="number"
          placeholder="Min Z-Score"
          value={filter.minZscore}
          onChange={(e) => { setFilter({ ...filter, minZscore: e.target.value }); setPage(0); }}
          className="px-3 py-2 rounded-lg text-sm w-32"
          style={{ backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: '#F4F4F5' }}
          step="0.5"
          min="0"
        />
        <span className="text-sm" style={{ color: '#64748B' }}>
          Showing {anomalies.length} of {total} anomalies
        </span>
      </div>

      {/* Anomalies Table */}
      <div 
        className="rounded-xl overflow-hidden"
        style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: '#64748B' }}>Symbol</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: '#64748B' }}>Date</th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase" style={{ color: '#64748B' }}>Position</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: '#64748B' }}>Signal</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: '#64748B' }}>Z-Score</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: '#64748B' }}>Entry</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: '#64748B' }}>1D</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: '#64748B' }}>5D</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: '#64748B' }}>10D</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: '#64748B' }}>30D</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: '#64748B' }}>60D</th>
              </tr>
            </thead>
            <tbody>
              {anomalies.map((anomaly, idx) => {
                // Determine position type from forward_returns or signal direction
                const positionType = anomaly.forward_returns?.position_type || 
                  (anomaly.daily_return >= 0 ? 'LONG' : 'SHORT');
                const isShort = positionType === 'SHORT';
                
                return (
                <tr 
                  key={anomaly._id || idx}
                  className="border-t transition-colors hover:bg-white/5"
                  style={{ borderColor: 'rgba(255,255,255,0.05)' }}
                >
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/stock/${anomaly.symbol}`)}
                      className="font-semibold hover:underline transition-colors"
                      style={{ color: '#60a5fa' }}
                      title={`View ${anomaly.symbol} details`}
                    >
                      {anomaly.symbol}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm" style={{ color: '#94A3B8' }}>
                    {anomaly.date}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span 
                      className="text-xs px-2 py-1 rounded font-semibold"
                      style={{ 
                        backgroundColor: isShort ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                        color: isShort ? '#ef4444' : '#22c55e'
                      }}
                    >
                      {positionType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {formatReturn(anomaly.daily_return)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm">
                    {anomaly.return_zscore?.toFixed(2) || '-'}σ
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-sm" style={{ color: '#94A3B8' }}>
                    ${anomaly.close_price?.toFixed(2) || '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {anomaly.forward_returns?.returns?.['1d']?.status === 'completed' 
                      ? formatReturn(anomaly.forward_returns.returns['1d'].return_pct)
                      : <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(251,191,36,0.2)', color: '#fbbf24' }}>Pending</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    {anomaly.forward_returns?.returns?.['5d']?.status === 'completed'
                      ? formatReturn(anomaly.forward_returns.returns['5d'].return_pct)
                      : <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(251,191,36,0.2)', color: '#fbbf24' }}>Pending</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    {anomaly.forward_returns?.returns?.['10d']?.status === 'completed'
                      ? formatReturn(anomaly.forward_returns.returns['10d'].return_pct)
                      : <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(251,191,36,0.2)', color: '#fbbf24' }}>Pending</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    {anomaly.forward_returns?.returns?.['30d']?.status === 'completed'
                      ? formatReturn(anomaly.forward_returns.returns['30d'].return_pct)
                      : <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(251,191,36,0.2)', color: '#fbbf24' }}>Pending</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    {anomaly.forward_returns?.returns?.['60d']?.status === 'completed'
                      ? formatReturn(anomaly.forward_returns.returns['60d'].return_pct)
                      : <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'rgba(251,191,36,0.2)', color: '#fbbf24' }}>Pending</span>
                    }
                  </td>
                </tr>
                );
              })}
              {anomalies.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center" style={{ color: '#64748B' }}>
                    No anomalies with return tracking found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm disabled:opacity-50 transition-colors hover:bg-white/10"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <span className="text-sm" style={{ color: '#64748B' }}>
              Page {page + 1} of {Math.ceil(total / limit)}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * limit >= total}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm disabled:opacity-50 transition-colors hover:bg-white/10"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReturnTrackingAdmin;
